import { inject, Injectable } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { DocumentTemplate, DocumentTemplateAddDTO, DocumentTemplateField, DocumentTemplateWithPath } from '../types/document-template';
import { DocumentTemplateService } from './document-template.service';

export interface TemplateExportData {
  name: string;
  description?: string;
  contentType: string;
  structure: { [key: string]: DocumentTemplateField };
  fieldsStructureId: string;
  anchors?: Record<string, unknown>;
  fileContent?: string;
  fileName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TemplateTransferService {
  private documentTemplateService = inject(DocumentTemplateService);

  /**
   * Exports a template to the clipboard as JSON.
   * Downloads the template file from its URL and includes the base64 content.
   * Strips runtime-only fields (id, createdAt, updatedAt, s3Key) to keep only the portable data.
   */
  async exportTemplate(template: DocumentTemplateWithPath): Promise<void> {
    let fileContent = '';
    let fileName = '';

    if (template.fileUrl) {
      try {
        const response = await fetch(template.fileUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const blob = await response.blob();
        fileContent = await this.blobToBase64(blob);
        // Extract filename from s3Key (e.g. "uploads/test.pdf" -> "test.pdf")
        fileName = template.s3Key ? template.s3Key.split('/').pop() || 'template-file' : 'template-file';
      } catch (err) {
        console.warn('Could not download template file for export:', err);
      }
    }

    const exportData: TemplateExportData = {
      name: template.name,
      description: template.description,
      contentType: template.contentType,
      structure: template.structure,
      fieldsStructureId: template.fieldsStructureId,
      ...(template.anchors ? { anchors: template.anchors } : {}),
      ...(fileContent ? { fileContent, fileName } : {})
    };

    const json = JSON.stringify(exportData, null, 2);
    await navigator.clipboard.writeText(json);
  }

  /**
   * Converts a Blob to a base64 string (without data URL prefix).
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        // Strip the data:...;base64, prefix
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Imports a template from the clipboard.
   * Reads clipboard text, validates the JSON structure, and creates the template via the API.
   */
  importTemplate(): Observable<{ documentTemplate: DocumentTemplate; uploadUrl: string }> {
    return from(this.readAndValidateClipboard()).pipe(
      switchMap(exportData => {
        const dto: DocumentTemplateAddDTO = {
          name: exportData.name,
          description: exportData.description || '',
          contentType: exportData.contentType,
          structure: exportData.structure,
          fieldsStructureId: exportData.fieldsStructureId,
          fileName: exportData.fileName || '',
          fileContent: exportData.fileContent || '',
          ...(exportData.anchors ? { anchors: exportData.anchors } : {})
        };
        return this.documentTemplateService.createDocumentTemplate(dto);
      })
    );
  }

  /**
   * Reads and validates template JSON from the clipboard.
   * Throws descriptive errors for invalid data.
   */
  async readAndValidateClipboard(): Promise<TemplateExportData> {
    const text = await navigator.clipboard.readText();

    if (!text || !text.trim()) {
      throw new Error('Clipboard is empty. Copy a template JSON first.');
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error('Clipboard does not contain valid JSON.');
    }

    return this.validate(parsed);
  }

  /**
   * Validates parsed JSON against the TemplateExportData shape.
   */
  validate(data: unknown): TemplateExportData {
    if (typeof data !== 'object' || data === null || Array.isArray(data)) {
      throw new Error('Invalid template format: expected a JSON object.');
    }

    const obj = data as Record<string, unknown>;

    if (typeof obj['name'] !== 'string' || !obj['name'].trim()) {
      throw new Error('Invalid template: "name" is required and must be a non-empty string.');
    }

    if (typeof obj['contentType'] !== 'string' || !obj['contentType'].trim()) {
      throw new Error('Invalid template: "contentType" is required and must be a non-empty string.');
    }

    if (typeof obj['fieldsStructureId'] !== 'string' || !obj['fieldsStructureId'].trim()) {
      throw new Error('Invalid template: "fieldsStructureId" is required and must be a non-empty string.');
    }

    if (typeof obj['structure'] !== 'object' || obj['structure'] === null || Array.isArray(obj['structure'])) {
      throw new Error('Invalid template: "structure" is required and must be an object.');
    }

    if (obj['description'] !== undefined && typeof obj['description'] !== 'string') {
      throw new Error('Invalid template: "description" must be a string if provided.');
    }

    if (
      obj['anchors'] !== undefined &&
      (typeof obj['anchors'] !== 'object' || obj['anchors'] === null || Array.isArray(obj['anchors']))
    ) {
      throw new Error('Invalid template: "anchors" must be an object if provided.');
    }

    return {
      name: obj['name'] as string,
      description: obj['description'] as string | undefined,
      contentType: obj['contentType'] as string,
      structure: obj['structure'] as TemplateExportData['structure'],
      fieldsStructureId: obj['fieldsStructureId'] as string,
      ...(obj['anchors'] ? { anchors: obj['anchors'] as Record<string, unknown> } : {}),
      ...(obj['fileContent'] ? { fileContent: obj['fileContent'] as string } : {}),
      ...(obj['fileName'] ? { fileName: obj['fileName'] as string } : {})
    };
  }
}
